import type { IntegrationConnection, Skill } from "@cohesive/api-client";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { ClearEditorPlugin } from "@lexical/react/LexicalClearEditorPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import {
  $getRoot,
  type EditorState,
  type EditorThemeClasses,
  type LexicalEditor,
} from "lexical";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "../../lib/utils";
import { ConnectionNode } from "../MessageInput/Lexical/ConnectionPlugin/ConnectionNode";
import { ConnectionPlugin } from "../MessageInput/Lexical/ConnectionPlugin/ConnectionPlugin";
import { PlaceholderPlugin } from "../MessageInput/Lexical/PlaceholderPlugin";
import { PlaceholderNode } from "../MessageInput/Lexical/PlaceholderPlugin/PlaceholderNode";
import { SkillNode } from "../MessageInput/Lexical/SkillPlugin/SkillNode";
import { SkillPlugin } from "../MessageInput/Lexical/SkillPlugin/SkillPlugin";
import {
  REPLACE_FROM_MARKDOWN_COMMAND,
  REPLACE_TEXT_COMMAND,
  TextReplacePlugin,
} from "../MessageInput/Lexical/TextReplacePlugin/TextReplacePlugin";
import {
  SelectionActionPlugin,
  type RichTextEditorSelectionAction,
} from "./Plugins/SelectionActionPlugin";
import { useLexicalEditableSync } from "./Plugins/useLexicalEditableSync";
import { richTextEditorTheme } from "./theme";
import { registerDashListMarkers } from "./utils/dashList";
import { getMarkdownTransformers } from "./utils/markdownTransformers";
import { serializeToMarkdown } from "./utils/serialization";

export interface RichTextEditorProps {
  value: string; // markdown
  onChange?: (markdown: string) => void;
  skills?: Skill[];
  integrationConnections?: IntegrationConnection[];
  placeholder?: string;
  showToolbar?: boolean;
  enableSkills?: boolean;
  enableEntities?: boolean;
  enablePlaceholders?: boolean;
  className?: string;
  /** Custom className for the ContentEditable element */
  editorClassName?: string;
  /** Custom className for the placeholder element */
  placeholderClassName?: string;
  autoFocus?: boolean;
  mode?: "rich" | "plain"; // New prop for plain vs rich text
  readOnly?: boolean;
  namespace?: string; // Unique namespace for multiple editors on same page
  onError?: (error: Error) => void; // Optional error handler
  /** Optional custom toolbar component - receives editor context */
  renderToolbar?: () => React.ReactNode;
  /** Called when the Lexical editor instance is ready; called with null on unmount */
  onEditorReady?: (editor: LexicalEditor | null) => void;
  /**
   * When false, external `value` updates are not applied after the first successful
   * sync (e.g. while the canvas label editor is focused — avoids Loro echo races).
   * Defaults to true.
   */
  controlledValueSync?: boolean;
  onEditorFocus?: React.FocusEventHandler<HTMLDivElement>;
  onEditorBlur?: React.FocusEventHandler<HTMLDivElement>;
  onEditorKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  /** Optional floating action on non-empty text selection (e.g. reference in chat). */
  selectionAction?: RichTextEditorSelectionAction;
  /**
   * Override the Lexical node→class theme. Defaults to `richTextEditorTheme`.
   * Pass `documentEditorTheme` (with `applyProseStyles={false}`) to render
   * markdown at the same scale as the TipTap document editor.
   */
  theme?: EditorThemeClasses;
  /**
   * Apply Tailwind Typography (`prose prose-sm`) to rich-mode content.
   * Defaults to true. Set false when supplying document-scale typography via
   * `theme` + a base font on `editorClassName`, so `prose-sm`'s 14px scale
   * doesn't override it.
   */
  applyProseStyles?: boolean;
}

/**
 * `LexicalComposer` reads `editable` from initialConfig on mount only; this
 * keeps the editor (and so the DOM `contenteditable` attribute rendered by
 * `ContentEditable`) in step with `readOnly` on every change.
 */
function EditableSyncPlugin({ editable }: { editable: boolean }) {
  useLexicalEditableSync(editable);
  return null;
}

function DashListPlugin() {
  const [editor] = useLexicalComposerContext();
  useEffect(() => registerDashListMarkers(editor), [editor]);
  return null;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = React.memo(
  ({
    value,
    onChange,
    skills = [],
    integrationConnections = [],
    placeholder = "Enter text...",
    showToolbar = true,
    enableSkills = false,
    enableEntities = false,
    enablePlaceholders = false,
    className,
    editorClassName,
    placeholderClassName,
    autoFocus = false,
    mode = "rich",
    readOnly = false,
    namespace,
    onError,
    renderToolbar,
    onEditorReady,
    controlledValueSync = true,
    onEditorFocus,
    onEditorBlur,
    onEditorKeyDown,
    selectionAction,
    theme = richTextEditorTheme,
    applyProseStyles = true,
  }) => {
    const [isInitialized, setIsInitialized] = useState(false);
    const suppressOnChangeRef = useRef(false);

    // Generate unique namespace if not provided
    const editorNamespace = useMemo(() => {
      return (
        namespace || `RichTextEditor-${Math.random().toString(36).slice(2, 9)}`
      );
    }, [namespace]);

    // Memoize the initialization callback to prevent unnecessary re-renders
    const handleInitialized = useCallback(() => {
      setIsInitialized(true);
    }, []);

    const transformers = useMemo(() => getMarkdownTransformers(), []);

    const initialConfig = useMemo(
      () => ({
        namespace: editorNamespace,
        nodes: [
          HeadingNode,
          QuoteNode,
          CodeNode,
          CodeHighlightNode,
          ListNode,
          ListItemNode,
          LinkNode,
          ConnectionNode,
          SkillNode,
          PlaceholderNode,
        ] as any,
        onError: (error: Error) => {
          if (onError) {
            onError(error);
          } else {
            console.error("RichTextEditor error:", error);
          }
        },
        theme,
        editable: !readOnly,
      }),
      [editorNamespace, readOnly, onError, theme],
    );

    return (
      <LexicalComposer initialConfig={initialConfig}>
        {onEditorReady ? (
          <EditorReadyPlugin onEditorReady={onEditorReady} />
        ) : null}
        <div
          className={cn(
            "border rounded-lg overflow-hidden flex flex-col",
            className,
          )}
        >
          {showToolbar && !readOnly && mode === "rich" && renderToolbar?.()}

          <div className="relative flex-1 min-h-0">
            {mode === "rich" ? (
              <RichTextPlugin
                placeholder={
                  <div
                    className={cn(
                      "absolute top-0 left-0 pointer-events-none px-4 py-3 text-muted-foreground",
                      placeholderClassName,
                    )}
                  >
                    {placeholder}
                  </div>
                }
                ErrorBoundary={LexicalErrorBoundary}
                contentEditable={
                  <ContentEditable
                    contentEditable={!readOnly}
                    className={cn(
                      "editor-input focus-visible:outline-none min-h-[200px] max-h-[600px] overflow-auto px-4 py-3",
                      applyProseStyles &&
                        "prose prose-sm dark:prose-invert max-w-none",
                      readOnly && "cursor-default select-none",
                      editorClassName,
                    )}
                    onFocus={onEditorFocus}
                    onBlur={onEditorBlur}
                    onKeyDown={onEditorKeyDown}
                  />
                }
              />
            ) : (
              <PlainTextPlugin
                placeholder={
                  <div
                    className={cn(
                      "absolute top-0 left-0 pointer-events-none px-4 py-3 text-muted-foreground",
                      placeholderClassName,
                    )}
                  >
                    {placeholder}
                  </div>
                }
                ErrorBoundary={LexicalErrorBoundary}
                contentEditable={
                  <ContentEditable
                    contentEditable={!readOnly}
                    className={cn(
                      "editor-input focus-visible:outline-none min-h-[200px] max-h-[600px] overflow-auto px-4 py-3",
                      readOnly && "cursor-default",
                      editorClassName,
                    )}
                    onFocus={onEditorFocus}
                    onBlur={onEditorBlur}
                    onKeyDown={onEditorKeyDown}
                  />
                }
              />
            )}

            {/* Plugins */}
            {/* Editable must settle before AutoFocusPlugin's effect runs (tree order). */}
            <EditableSyncPlugin editable={!readOnly} />
            <HistoryPlugin />
            <ClearEditorPlugin />
            {autoFocus && <AutoFocusPlugin />}
            <TextReplacePlugin
              markdownTransformers={
                mode === "rich" ? (transformers as any) : undefined
              }
            />

            {mode === "rich" && (
              <>
                <ListPlugin />
                <DashListPlugin />
                <TabIndentationPlugin />
                <MarkdownShortcutPlugin transformers={transformers as any} />
              </>
            )}

            {enableSkills && <SkillPlugin enabled={true} skills={skills} />}

            {enableEntities && (
              <ConnectionPlugin
                enabled={true}
                connections={integrationConnections}
              />
            )}

            {enablePlaceholders && (
              <PlaceholderPlugin enabled={true} mode="edit" />
            )}

            {selectionAction ? (
              <SelectionActionPlugin
                enabled={selectionAction.enabled}
                onSelection={selectionAction.onSelection}
                renderPill={selectionAction.renderPill}
              />
            ) : null}

            <OnChangePlugin
              ignoreSelectionChange={true}
              onChange={(editorState: EditorState) => {
                if (suppressOnChangeRef.current) {
                  suppressOnChangeRef.current = false;
                  return;
                }
                if (!onChange || !isInitialized) return;
                if (mode === "rich") {
                  // Serialize to markdown
                  const markdown = serializeToMarkdown(
                    editorState,
                    transformers,
                  );
                  onChange(markdown);
                } else {
                  // Plain text mode - just get text content
                  let text = "";
                  editorState.read(() => {
                    const root = editorState._nodeMap.get("root");
                    if (root) {
                      text = root.getTextContent();
                    }
                  });
                  onChange(text);
                }
              }}
            />

            <ControlledValueCommandSyncPlugin
              value={value}
              mode={mode}
              transformers={transformers}
              suppressOnChangeRef={suppressOnChangeRef}
              onInitialized={handleInitialized}
              controlledValueSync={controlledValueSync}
            />
          </div>
        </div>
      </LexicalComposer>
    );
  },
);

function EditorReadyPlugin({
  onEditorReady,
}: {
  onEditorReady: (editor: LexicalEditor | null) => void;
}) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    onEditorReady(editor);
    return () => {
      onEditorReady(null);
    };
  }, [editor, onEditorReady]);
  return null;
}

function getSerializedValue(
  editor: LexicalEditor,
  mode: "rich" | "plain",
  transformers: any[],
): string {
  if (mode === "rich") {
    return serializeToMarkdown(editor.getEditorState(), transformers);
  }
  let text = "";
  editor.getEditorState().read(() => {
    text = $getRoot().getTextContent();
  });
  return text;
}

/**
 * Keeps the Lexical document in sync with the controlled `value` prop by
 * dispatching TextReplacePlugin commands instead of calling deserialize/update
 * directly. Skips when the editor already matches `value`; when
 * `controlledValueSync` is false after the first sync, external `value` updates
 * are ignored until sync is re-enabled.
 */
function ControlledValueCommandSyncPlugin({
  value,
  mode,
  transformers,
  suppressOnChangeRef,
  onInitialized,
  controlledValueSync = true,
}: {
  value: string;
  mode: "rich" | "plain";
  transformers: any[];
  suppressOnChangeRef: React.MutableRefObject<boolean>;
  onInitialized: () => void;
  controlledValueSync?: boolean;
}) {
  const [editor] = useLexicalComposerContext();
  const didCallOnInitialized = useRef(false);
  const hasCompletedInitialSyncRef = useRef(false);
  /** `value` prop when controlled sync was turned off (e.g. editor focus). */
  const valueWhenSyncPausedRef = useRef<string | undefined>(undefined);
  const prevControlledValueSyncRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (!editor) return;

    try {
      const prev = prevControlledValueSyncRef.current;
      const enteredPauseSync =
        !controlledValueSync && (prev === null ? false : prev);
      if (enteredPauseSync) {
        valueWhenSyncPausedRef.current = value;
      }
      prevControlledValueSyncRef.current = controlledValueSync;

      const current = getSerializedValue(editor, mode, transformers);
      if (current === value) {
        if (!didCallOnInitialized.current) {
          didCallOnInitialized.current = true;
          onInitialized();
        }
        hasCompletedInitialSyncRef.current = true;
        return;
      }

      if (!controlledValueSync && hasCompletedInitialSyncRef.current) {
        return;
      }

      // Prop unchanged since sync was paused (e.g. blur before debounced save) — do not
      // replace editor with stale `value` (fixes canvas label + Loro debounce).
      if (
        hasCompletedInitialSyncRef.current &&
        value === valueWhenSyncPausedRef.current
      ) {
        return;
      }

      suppressOnChangeRef.current = true;
      if (mode === "rich") {
        editor.dispatchCommand(REPLACE_FROM_MARKDOWN_COMMAND, {
          markdown: value,
        });
      } else {
        editor.dispatchCommand(REPLACE_TEXT_COMMAND, { text: value });
      }

      queueMicrotask(() => {
        if (suppressOnChangeRef.current) {
          suppressOnChangeRef.current = false;
        }
      });

      if (!didCallOnInitialized.current) {
        didCallOnInitialized.current = true;
        onInitialized();
      }
      hasCompletedInitialSyncRef.current = true;
    } finally {
      // Only meaningful while sync is on; avoids blocking later updates when `value`
      // coincidentally matches the snapshot from a past pause (e.g. "" after blur).
      if (controlledValueSync) {
        valueWhenSyncPausedRef.current = undefined;
      }
    }
  }, [
    value,
    mode,
    transformers,
    editor,
    onInitialized,
    suppressOnChangeRef,
    controlledValueSync,
  ]);

  return null;
}
